import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Header from "../components/Header";
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api";

export default function ClaimedVideos() {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchClaims();
  }, []);

  const fetchClaims = async () => {
    setError(false);
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/creator-claims/my-claims?_t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClaims(res.data);
    } catch (err) {
      console.error("Failed to fetch claims:", err);
      if (err.response?.status === 401) {
        localStorage.removeItem("token");
        window.location.href = "/login";
        return;
      }
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      claimed: "bg-yellow-100 text-yellow-800",
      completed: "bg-green-100 text-green-800",
      published: "bg-blue-100 text-blue-800"
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  // Only show active claims (status = 'claimed')
  const activeClaims = claims.filter(c => c.status === 'claimed');

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Loading your claims...</div>;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <p className="text-gray-600 mb-4">Failed to load claims.</p>
        <button onClick={fetchClaims} className="bg-primary text-white px-4 py-2 rounded-md">Retry</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-primary">My Active Claims</h1>
          <button
            onClick={fetchClaims}
            className="text-sm text-gray-500 hover:text-primary transition flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>

        {/* Active claim count */}
        {activeClaims.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-2 rounded-md mb-4">
            You have {activeClaims.length} active claim{activeClaims.length > 1 ? 's' : ''}. You can claim up to 3.
          </div>
        )}

        {activeClaims.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            <p>No active claims. You can claim up to 3 videos.</p>
            <Link to="/available-requests" className="text-primary hover:underline mt-2 inline-block">Browse available requests →</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activeClaims.map(claim => (
              <div key={claim.id} className="bg-white rounded-lg shadow p-5">
                <div className="text-xl font-bold text-primary mb-2">{claim.verse}</div>

                {claim.description && (
                  <div className="text-sm text-gray-600 mt-2 mb-3 italic bg-gray-50 p-2 rounded-md">
                    <span className="font-semibold">User confusion:</span>
                    <p className="mt-1">“{claim.description}”</p>
                  </div>
                )}

                <div className="text-sm text-gray-500 mb-3">Claimed: {new Date(claim.claimed_at).toLocaleDateString()}</div>
                <div className="mb-3">
                  <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(claim.status)}`}>
                    {claim.status}
                  </span>
                </div>

                {claim.status === "claimed" && (
                  <Link
                    to="/submit"
                    state={{ verse: claim.verse, claimId: claim.id }}
                    className="block text-center bg-primary text-white py-2 rounded-md font-semibold hover:bg-opacity-90 transition"
                  >
                    Upload Video →
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}